import React, { useState, useRef, SetStateAction, RefObject, type Dispatch, useMemo } from 'react';
import { useClickOutside } from '../../../hooks/utilities/useClickOutside';
import { SelectMenuSearchInput } from './SelectMenuSearchInput';
import { SelectMenuList } from './SelectMenuList';
import type { MenuItem, LiliComboboxWithInputValueConstraint } from './SelectMenuItem';

interface SelectMenuProps<T extends LiliComboboxWithInputValueConstraint> {
  id: string;
  items: MenuItem<T>[];
  selectedValue: T;
  onItemSelected: (value: T) => void;
  setIsSelectOpen: Dispatch<SetStateAction<boolean>>;
  elTriggerButton: RefObject<HTMLButtonElement>;
  inputPlaceholder?: string;
  InputPrefixIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

export const SelectMenu = <T extends LiliComboboxWithInputValueConstraint>({
  id,
  items,
  selectedValue,
  onItemSelected,
  setIsSelectOpen,
  elTriggerButton,
  inputPlaceholder,
  InputPrefixIcon,
}: SelectMenuProps<T>) => {
  const [filterKeyword, setFilterKeyword] = useState<string>('');
  const elDrawer = useRef<HTMLDivElement>(null);

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const value = typeof item.value === 'string' ? item.value : item.value.toString();
        return (
          item.title.toLowerCase().includes(filterKeyword.toLowerCase()) ||
          item.itemDescription?.toLowerCase().includes(filterKeyword.toLowerCase()) ||
          value.toString().includes(filterKeyword.toLowerCase())
        );
      }),
    [filterKeyword],
  );

  const onClickOutside = () => setIsSelectOpen(false);

  useClickOutside({
    el: elDrawer,
    excludeEls: [elTriggerButton],
    onClickOutside,
  });

  const handleItemSelection = (value: T) => {
    onItemSelected(value);
    setIsSelectOpen(false);
  };

  return (
    <div
      ref={elDrawer}
      className='flex flex-col gap-[6px] shadow-xl z-99999 absolute left-0 bottom-0 translate-y-[calc(100%+5px)] w-full border border-bw00 px-[8px] rounded-[8px] py-[8px] fadeIn bg-bw01'
    >
      <SelectMenuSearchInput
        id={id}
        value={filterKeyword}
        onValueChange={setFilterKeyword}
        placeholder={inputPlaceholder}
        PrefixIcon={InputPrefixIcon}
      />
      <SelectMenuList
        id={id}
        items={filteredItems}
        selectedValue={selectedValue}
        onItemClick={handleItemSelection}
        searchWord={filterKeyword}
      />
    </div>
  );
};
